import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@shared/components/ui/LanguageSwitcher';

export default function Navigation() {
  const { t } = useTranslation();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the drawer when the user navigates or presses Escape.
  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.hash]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while the drawer is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [menuOpen]);

  return (
    <nav className="landing-nav" aria-label="Primary">
      <Link to="/" className="logo" aria-label="Heka.care home">
        <img
          src="/logo.png"
          srcSet="/logo-small.png 240w, /logo-email.png 480w, /logo.png 1160w"
          sizes="(max-width: 640px) 150px, 220px"
          alt="Heka.care — Telehealth for Ghana"
          width="220"
          height="52"
          className="landing-nav-logo"
          decoding="async"
        />
      </Link>

      <ul className="nav-links" aria-label="Marketing navigation">
        <li><Link to="/">{t('nav.home')}</Link></li>
        <li><a href="/#features">{t('nav.features')}</a></li>
        <li><Link to="/shop">{t('nav.shop')}</Link></li>
        <li><a href="/#providers">{t('nav.providers')}</a></li>
        <li><a href="/#ai">{t('nav.ai')}</a></li>
        <li><Link to="/legal/nhis-compliance">{t('nav.nhis')}</Link></li>
      </ul>

      <div className="nav-ctas">
        <LanguageSwitcher />
        <Link to="/login" className="btn btn-ghost">{t('nav.signIn')}</Link>
        <Link to="/register" className="btn btn-primary">{t('nav.getStarted')}</Link>
      </div>

      {/* Mobile: always-visible "Sign up" CTA + hamburger toggle */}
      <div className="nav-mobile-tools">
        <Link to="/register" className="btn btn-primary nav-mobile-cta">{t('nav.getStarted')}</Link>
        <button
          type="button"
          className="nav-burger"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen ? 'true' : 'false'}
          aria-controls="marketing-mobile-menu"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <span /><span /><span />
        </button>
      </div>

      {/* Drawer + backdrop are portaled to <body> so they escape the nav's
          fixed-position stacking context and always overlay every landing
          section, including any element with its own z-index. */}
      {menuOpen && createPortal(
        <>
          <button
            type="button"
            className="nav-drawer-backdrop"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />
          <aside
            id="marketing-mobile-menu"
            className="nav-drawer open"
            aria-label="Mobile navigation"
          >
            <div className="nav-drawer-head">
              <span className="nav-drawer-title">Menu</span>
              <button type="button" className="nav-drawer-close" onClick={() => setMenuOpen(false)} aria-label="Close menu">{'\u2715'}</button>
            </div>
            <ul className="nav-drawer-links">
              <li><Link to="/" onClick={() => setMenuOpen(false)}>{t('nav.home')}</Link></li>
              <li><a href="/#features" onClick={() => setMenuOpen(false)}>{t('nav.features')}</a></li>
              <li><Link to="/shop" onClick={() => setMenuOpen(false)}>{t('nav.shop')}</Link></li>
              <li><a href="/#providers" onClick={() => setMenuOpen(false)}>{t('nav.providers')}</a></li>
              <li><Link to="/register?role=facility" onClick={() => setMenuOpen(false)}>{t('nav.facilities')}</Link></li>
              <li><a href="/#ai" onClick={() => setMenuOpen(false)}>{t('nav.ai')}</a></li>
              <li><Link to="/legal/nhis-compliance" onClick={() => setMenuOpen(false)}>{t('nav.nhis')}</Link></li>
              <li><Link to="/how-it-works" onClick={() => setMenuOpen(false)}>How it works</Link></li>
              <li><Link to="/for-patients" onClick={() => setMenuOpen(false)}>For patients</Link></li>
              <li><Link to="/for-doctors" onClick={() => setMenuOpen(false)}>For doctors</Link></li>
              <li><Link to="/faq" onClick={() => setMenuOpen(false)}>FAQ</Link></li>
            </ul>
            <div className="nav-drawer-ctas">
              <LanguageSwitcher variant="light" />
              <Link to="/login" className="btn btn-ghost" onClick={() => setMenuOpen(false)}>{t('nav.signIn')}</Link>
              <Link to="/register" className="btn btn-primary" onClick={() => setMenuOpen(false)}>{t('nav.getStarted')}</Link>
            </div>
            <p className="nav-drawer-footer">For emergencies dial <strong>112</strong> or <strong>193</strong>.</p>
          </aside>
        </>,
        document.body,
      )}
    </nav>
  );
}
